import PropTypes from 'prop-types';
import React, { memo, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Map, List } from 'immutable';
import dayjs from 'dayjs';
import classNames from 'classnames';

import { Collapse } from '../../helpers/Collapse';
import { ExpandableText } from '../../helpers/ExpandableText';
import { IconButton } from '../../helpers/IconButton';
import { Spinner } from '../../helpers/Spinner';
import { getIn } from '../../../utils/ChangeSpy';
import { DATE_FORMAT } from '../../../utils/dateFormat';

import style from './style.module.css';

const EMPTY_VALUE = '—';

const MetaItem = ({ label, isClamped = false, isFluid = false, children }) => (
  <div className={classNames(style.metaItem, { [style.fluid]: isFluid })}>
    <span className={style.label}>{label}</span>
    <span className={classNames(style.value, { [style.clamped]: isClamped })}>{children || EMPTY_VALUE}</span>
  </div>
);

MetaItem.propTypes = {
  children: PropTypes.node,
  isClamped: PropTypes.bool,
  isFluid: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

/**
 * Read-only executive project details widget with collapsible scope.
 *
 * @param props {Object}.
 * @param props.projectData {Immutable.Map} Project information data.
 * @param props.users {Immutable.List} User list.
 * @param props.harvcoTags {Immutable.List} Harvco tag list.
 * @param props.common {Immutable.Map} Common object.
 * @param props.onEdit {Function} Handle event of opening the edit popup.
 */
const EProjectDetailsComponent = ({ projectData, users, harvcoTags, common, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded(previous => !previous);
  }, []);

  const scope = projectData.getIn(['details', 'name']);
  const createdDate = dayjs(projectData.get('dateCreated'));
  const isActive = projectData.getIn(['status', 'value']);
  const type = projectData.getIn(['projectType', 'name']);
  const clientName = projectData.get('abbrName');
  const industryNames = common
    .get('bic')
    .concat(common.get('bm'))
    .map(industry => industry.get('name'))
    .join(', ');
  const harvcoTagNames = harvcoTags
    .map(tag => getIn(tag, 'tag'))
    .filter(Boolean)
    .join(', ');
  const userNames = users
    .filter(user => user.get('id') > 0)
    .map(user => user.get('name'))
    .join(', ');

  return (
    <section className={style.host}>
      <div className={style.header}>
        <h2 className={style.title}>{projectData.getIn(['category', 'name']) || 'Executive Project'}</h2>
        <span className={classNames('label', isActive ? 'label-success' : 'label-default')}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
        {type && <span className="label label-muted">{type}</span>}
        <div className={style.actions}>
          <IconButton icon="pencil" onClick={onEdit} title="Edit details" />
        </div>
      </div>
      <div className={style.meta}>
        <MetaItem label="Client">
          {clientName && (
            <Link target="_blank" to={`/company/${projectData.get('buyerId')}/buyer`}>
              {clientName}
            </Link>
          )}
        </MetaItem>
        <MetaItem label="HarvCo Lead">{projectData.getIn(['harvcoLead', 'name'])}</MetaItem>
        <MetaItem label="Client Lead">{projectData.getIn(['clientLead', 'name'])}</MetaItem>
        <MetaItem label="Type">{type}</MetaItem>
        <MetaItem label="Created">{createdDate.isValid() ? createdDate.format(DATE_FORMAT) : null}</MetaItem>
        <MetaItem isClamped={!isExpanded} label="Industries" isFluid>
          {industryNames}
        </MetaItem>
        <MetaItem isClamped={!isExpanded} label="Harvco Tags" isFluid>
          {harvcoTagNames}
        </MetaItem>
        <MetaItem isClamped={!isExpanded} label="Users" isFluid>
          {userNames}
        </MetaItem>
      </div>
      <Collapse isOpen={isExpanded}>
        <div className={style.texts}>
          <MetaItem label="Project Scope">{scope && <ExpandableText>{scope}</ExpandableText>}</MetaItem>
        </div>
      </Collapse>
      <div className={style.toggle}>
        <IconButton
          aria-expanded={isExpanded}
          className={classNames(style.chevron, { [style.chevronUp]: isExpanded })}
          icon="chevron-down"
          onClick={handleToggle}
          size="sm"
          title={isExpanded ? 'Hide details' : 'Show all details'}
        />
      </div>
      {common.get('projectLoading') && <Spinner />}
    </section>
  );
};

EProjectDetailsComponent.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  harvcoTags: PropTypes.instanceOf(List).isRequired,
  onEdit: PropTypes.func.isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

export const EProjectDetails = memo(EProjectDetailsComponent);
